import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { apiClient } from './api-client'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Download a file from a URL (including S3 URLs)
 * @param url The URL to download from
 * @param filename The name to save the file as
 */
export async function downloadFile(url: string, filename: string) {
  try {
    console.log('Starting download from:', url)
    
    // Method 1: Try using backend as proxy (most reliable for S3)
    try {
      console.log('Trying download via backend proxy...')
      const response = await apiClient.downloadFile(url)
      
      if (response.data) {
        const blob = response.data as Blob
        const blobUrl = window.URL.createObjectURL(blob)
        
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = filename
        link.style.display = 'none'
        
        document.body.appendChild(link)
        link.click()
        
        setTimeout(() => {
          document.body.removeChild(link)
          window.URL.revokeObjectURL(blobUrl)
        }, 100)
        
        console.log('✓ Download successful via backend proxy')
        return
      }
    } catch (proxyError) {
      console.warn('Backend proxy method failed, trying direct fetch:', proxyError)
    }
    
    // Method 2: Try using fetch with proper CORS handling
    try {
      console.log('Trying download via direct fetch...')
      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        credentials: 'omit',
      })
      
      if (response.ok) {
        const blob = await response.blob()
        const blobUrl = window.URL.createObjectURL(blob)
        
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = filename
        link.style.display = 'none'
        
        document.body.appendChild(link)
        link.click()
        
        setTimeout(() => {
          document.body.removeChild(link)
          window.URL.revokeObjectURL(blobUrl)
        }, 100)
        
        console.log('✓ Download successful via direct fetch')
        return
      }
    } catch (fetchError) {
      console.warn('Direct fetch method failed, trying S3 header method:', fetchError)
    }
    
    // Method 3: Try adding response-content-disposition header to S3 URL
    try {
      console.log('Trying download via S3 headers...')
      const urlWithHeader = new URL(url)
      if (!urlWithHeader.searchParams.has('response-content-disposition')) {
        urlWithHeader.searchParams.append(
          'response-content-disposition',
          `attachment; filename="${filename}"`
        )
      }
      
      const response = await fetch(urlWithHeader.toString(), {
        method: 'GET',
        mode: 'cors',
      })
      
      if (response.ok) {
        const blob = await response.blob()
        const blobUrl = window.URL.createObjectURL(blob)
        
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = filename
        link.style.display = 'none'
        
        document.body.appendChild(link)
        link.click()
        
        setTimeout(() => {
          document.body.removeChild(link)
          window.URL.revokeObjectURL(blobUrl)
        }, 100)
        
        console.log('✓ Download successful via S3 headers')
        return
      }
    } catch (headerError) {
      console.warn('S3 header method failed, using fallback:', headerError)
    }
    
    // Method 4: Fallback - use direct link with download attribute
    console.log('Using fallback method (direct link)...')
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    link.setAttribute('target', '_blank')
    link.setAttribute('rel', 'noreferrer')
    link.style.display = 'none'
    
    document.body.appendChild(link)
    link.click()
    
    setTimeout(() => {
      document.body.removeChild(link)
    }, 100)
    
    console.log('✓ Download initiated via direct link (fallback)')
    
  } catch (error) {
    console.error('Download error:', error)
    throw new Error(
      `Erro ao fazer download do arquivo: ${
        error instanceof Error ? error.message : 'Desconhecido'
      }`
    )
  }
}

